import { ModelMetadataEntry } from "@/queries/models/useModelMetadata";

/**
 * Normalize a model name to a canonical form for comparison across naming conventions.
 *
 * Strips (in order):
 *   1. Bedrock regional prefixes: us., eu., ap., global.
 *   2. DeepSeek Bedrock prefix: deepseek. → deepseek- (deepseek is part of the OR model name)
 *   3. Other embedded provider prefixes: anthropic., google., meta., etc.
 *   4. VertexAI @ date separator: @YYYYMMDD → -YYYYMMDD
 *   5. Bedrock version+colon suffix: -vN:M or -N:M  (e.g. -v1:0, -1:0)
 *   6. Date suffixes (longest first): YYYY-MM-DD, YYYYMMDD, MM-YYYY, YYYY/YYMM, MM-DD
 *   7. Generic version suffix after dates: -vN, -vN.M  (e.g. -v2, -v0.1)
 *   8. "-latest" suffix
 *   9. Mistral "open-" prefix
 *  10. "-instruct" suffix (and -instruct-vX.Y)
 *  11. Alpha-digit boundary: llama3 → llama-3 (when digit precedes a separator or end)
 *  12. Version dot: X-Y → X.Y (single digit after separator/end, e.g. claude-3-7 → claude-3.7)
 */
const normalizeModelName = (name: string): string => {
  let n = name.toLowerCase();

  n = n.replace(/^(?:us|eu|ap|global)\./, "");
  n = n.replace(/^deepseek\./, "deepseek-");
  n = n.replace(/^(?:anthropic|mistral|google|meta|cohere|amazon|ai21|openai|mistralai)\./, "");
  n = n.replace(/@(\d{8})$/, "-$1");
  n = n.replace(/-v?\d+:\d+$/, "");

  // Date stripping must happen BEFORE generic version suffix (e.g. -v2 in "sonnet-v2@date")
  n = n.replace(/-latest$/, "");
  n = n.replace(/-\d{4}-\d{2}-\d{2}$/, ""); // YYYY-MM-DD
  n = n.replace(/-\d{8}$/, ""); // YYYYMMDD
  n = n.replace(/-\d{2}-\d{4}$/, ""); // MM-YYYY (e.g. command-a-03-2025)
  n = n.replace(/-\d{4}$/, ""); // YYYY or YYMM (e.g. mistral-large-2411)
  n = n.replace(/-\d{2}-\d{2}$/, ""); // MM-DD date fragment (e.g. gemini-2.5-flash-lite-preview-06-17)

  // Generic version suffix - after date stripping so "model-v2@date" works correctly
  n = n.replace(/-v[\d.]+$/, "");

  n = n.replace(/^open-/, "");
  n = n.replace(/-instruct(?:-v[\d.]+)?$/, "");

  // Insert hyphen between a run of letters and a digit when the digit is before a separator
  // or end of string: llama3-3-70b → llama-3-3-70b, gemma3 → gemma-3
  // Avoids changing gpt-4o (4 is followed by letter o, not separator)
  n = n.replace(/([a-z]+)(\d+)(?=[-.]|$)/g, "$1-$2");

  // Convert digit-dash-single-digit to digit.digit when the second digit is before a
  // separator or end: claude-3-7-sonnet → claude-3.7-sonnet, 4-5 → 4.5
  // Skips cases like 3-3b (second digit followed by letter like 'b' = billion)
  n = n.replace(/(\d)-(\d)(?=[-.]|$)/g, "$1.$2");

  return n;
};

/**
 * Tokenize a normalized model name into a set of meaningful parts.
 * Splits on hyphens and slashes only — dots are preserved so version numbers
 * like "3.7" and "4.5" stay as single tokens and don't create false matches.
 */
const tokenize = (name: string): Set<string> =>
  new Set(name.split(/[-/]+/).filter(t => t.length > 0));

/**
 * Jaccard similarity between two normalized model name strings.
 * Returns a value in [0, 1]; higher means more overlap.
 */
const jaccardSimilarity = (a: string, b: string): number => {
  const tokA = tokenize(a);
  const tokB = tokenize(b);
  let intersection = 0;
  for (const t of tokA) {
    if (tokB.has(t)) {
      intersection += 1;
    }
  }
  const union = tokA.size + tokB.size - intersection;
  return union === 0 ? 1 : intersection / union;
};

const FUZZY_THRESHOLD = 0.65;

/**
 * Look up OpenRouter metadata for a model (exact, normalized, suffix, then fuzzy match).
 */
export const getModelMetadata = (
  providerName: string,
  modelName: string,
  metadataMap: Record<string, ModelMetadataEntry> | undefined,
  providerMap: Record<string, string> | undefined,
): ModelMetadataEntry | null => {
  if (!metadataMap || !modelName) {
    return null;
  }

  // Pass 0: model name may already be a full OpenRouter id (e.g. openai/gpt-4o).
  if (metadataMap[modelName]) {
    return metadataMap[modelName];
  }

  const openrouterPrefix = providerMap?.[providerName];

  // Pass 1: exact key with provider prefix
  if (openrouterPrefix) {
    const key = `${openrouterPrefix}/${modelName}`;
    if (metadataMap[key]) {
      return metadataMap[key];
    }
  }

  // Pass 2: normalized key with provider prefix
  const normalizedName = normalizeModelName(modelName);
  if (openrouterPrefix) {
    const key = `${openrouterPrefix}/${normalizedName}`;
    if (metadataMap[key]) {
      return metadataMap[key];
    }
  }

  // Passes 3–5: scan all OR entries, skipping variant suffixes like :thinking
  let normalizedMatch: ModelMetadataEntry | null = null;
  let fuzzyMatch: ModelMetadataEntry | null = null;
  let fuzzyScore = 0;

  for (const [id, entry] of Object.entries(metadataMap)) {
    if (id.includes(":")) {
      continue;
    }

    const slashIdx = id.indexOf("/");
    const suffix = slashIdx >= 0 ? id.slice(slashIdx + 1) : id;

    // Pass 3: exact suffix
    if (suffix === modelName) {
      return entry;
    }

    const normalizedSuffix = normalizeModelName(suffix);

    // Pass 4: normalized suffix exact match
    if (!normalizedMatch && normalizedSuffix === normalizedName) {
      normalizedMatch = entry;
      // Don't break — continue so fuzzy is fully computed if normalizedMatch is ever null
      // (but once set, we skip fuzzy scoring below)
    }

    // Pass 5: Jaccard fuzzy (only while we haven't found a normalized match)
    if (!normalizedMatch) {
      const score = jaccardSimilarity(normalizedName, normalizedSuffix);
      if (score > fuzzyScore && score >= FUZZY_THRESHOLD) {
        fuzzyScore = score;
        fuzzyMatch = entry;
      }
    }
  }

  return normalizedMatch ?? fuzzyMatch;
};

export const formatContextLength = (tokens: number | null | undefined): string | null => {
  if (tokens === null || tokens === undefined) {
    return null;
  }
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${Math.round(tokens / 1_000)}K`;
  }
  return `${tokens}`;
};

export const formatPrice = (pricePerMillion: number | null | undefined): string | null => {
  if (pricePerMillion === null || pricePerMillion === undefined) {
    return null;
  }
  if (pricePerMillion === 0) {
    return "Free";
  }
  if (pricePerMillion < 0.01) {
    return `$${pricePerMillion.toFixed(4)}`;
  }
  return `$${pricePerMillion.toFixed(2)}`;
};
